import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, merge, of, shareReplay, switchMap, timer } from 'rxjs';
import { ApiOkResult } from '@app/models/ApiRespuesta.interface';
import { Notificacion } from '@app/shared/interfaces/notification.interface';
import { environment } from 'src/environments/environment';
import { handleError } from '../tools/handle-error';

// Intervalo de polling del badge de notificaciones (ms). 30s: suficiente para "actualizable" sin saturar el back.
const POLL_INTERVAL_MS = 30000;

@Injectable({
    providedIn: 'root'
})
export class NotificacionesService {
    private readonly http = inject(HttpClient);
    private readonly url = environment.url + 'notificaciones';

    // Dispara un refresco manual (ej: al abrir el popover o tras marcar como leída)
    private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);

    /** Stream compartido: se actualiza solo por polling + cada vez que se llama refresh(). */
    readonly unreadCount$: Observable<number> = merge(timer(0, POLL_INTERVAL_MS), this.refreshTrigger$).pipe(
        switchMap(() => this.getUnreadCount().pipe(catchError(() => of(0)))),
        shareReplay({ bufferSize: 1, refCount: false })
    );

    /** Forzar un refresco inmediato (ej: al abrir el panel o tras marcar como leída). */
    refresh(): void {
        this.refreshTrigger$.next();
    }

    getAll(page = 1, perPage = 50): Observable<{ entities: Notificacion[]; count: number }> {
        const params = new HttpParams().set('page', page).set('perPage', perPage);
        return this.http.get<ApiOkResult<{ entities: any[]; count: number }>>(this.url, { params }).pipe(
            catchError((err) => handleError(err)),
            map((data) => ({
                entities: (data.result?.entities ?? []).map((raw) => this.mapNotificacion(raw)),
                count: data.result?.count ?? 0
            }))
        );
    }

    // El back persiste "visto" como tinyint (0/1); lo normalizamos a boolean para el front.
    private mapNotificacion(raw: any): Notificacion {
        return {
            id: raw.id,
            id_visible: raw.id_visible,
            tipo: raw.tipo,
            titulo: raw.titulo,
            texto: raw.texto,
            fecha_hora: new Date(raw.fecha_hora),
            visto: !!raw.visto,
            url: raw.url ?? undefined,
            emisor: raw.emisor ?? undefined,
            modulo: raw.modulo ?? undefined
        };
    }

    getUnreadCount(): Observable<number> {
        return this.http.get<ApiOkResult<number>>(`${this.url}/no-leidas/count`).pipe(
            catchError((err) => handleError(err)),
            map((data) => data.result)
        );
    }

    markAsRead(id: string): Observable<void> {
        return this.http.patch<ApiOkResult<unknown>>(`${this.url}/${id}/leida`, {}).pipe(
            catchError((err) => handleError(err)),
            map(() => undefined)
        );
    }

    markAsUnread(id: string): Observable<void> {
        return this.http.patch<ApiOkResult<unknown>>(`${this.url}/${id}/no-leida`, {}).pipe(
            catchError((err) => handleError(err)),
            map(() => undefined)
        );
    }

    toggleRead(notif: Notificacion): Observable<void> {
        return notif.visto ? this.markAsUnread(notif.id) : this.markAsRead(notif.id);
    }

    markAllAsRead(): Observable<void> {
        return this.http.patch<ApiOkResult<unknown>>(`${this.url}/marcar-todas-leidas`, {}).pipe(
            catchError((err) => handleError(err)),
            map(() => undefined)
        );
    }
}
